<template>
  <div class="eat-detail-page">
    <CultureNav />
    <main v-if="eatery">
      <v-container>
        <v-btn outlined class="back-link" :to="{ name: 'reckon-way-eats' }">
          <v-icon left>mdi-arrow-left</v-icon>All Eats
        </v-btn>
        <article class="venue-card">
          <v-img
            :src="(eatery.image || fallbackImage) | mediaSrc"
            :alt="`${eatery.name} from ${primaryLocation}`"
            class="experience-image"
            min-height="320"
            max-height="560"
          ></v-img>
          <div class="venue-content">
            <p class="kicker">{{ eatery.type }} · Venue</p>
            <h1>{{ eatery.name }}</h1>
            <p class="venue-summary">{{ eatery.description }}</p>
            <dl class="venue-facts">
              <div v-if="primaryLocation"><dt>Location</dt><dd>{{ primaryLocation }}</dd></div>
              <div v-if="eatery.address"><dt>Address</dt><dd>{{ eatery.address }}</dd></div>
              <div v-if="eatery.visitDates.length"><dt>Date visited</dt><dd>{{ eatery.visitDates.join(' · ') }}</dd></div>
              <div v-if="eatery.journeys.length"><dt>Trip</dt><dd>{{ eatery.journeys.join(' · ') }}</dd></div>
              <div><dt>Visits</dt><dd>{{ eatery.visitCountLabel || eatery.visitCount }}</dd></div>
              <div v-if="eatery.michelinStars"><dt>Recognition</dt><dd>{{ starLabel }}</dd></div>
            </dl>
            <div class="venue-actions">
              <v-btn v-if="eatery.timelineHref" color="primary" :to="eatery.timelineHref">
                <v-icon left>mdi-timeline-outline</v-icon>Find It in the Trip Chronology
              </v-btn>
              <v-btn v-else-if="eatery.storyHref" color="primary" :to="eatery.storyHref">
                <v-icon left>mdi-book-open-page-variant</v-icon>View the Trip Story
              </v-btn>
              <v-btn v-if="eatery.websiteUrl" outlined :href="eatery.websiteUrl" target="_blank" rel="noopener noreferrer">
                Venue Website <v-icon right small>mdi-open-in-new</v-icon>
              </v-btn>
            </div>
            <router-link v-if="eatery.timelineHref && eatery.storyHref" :to="eatery.storyHref" class="direct-story-link">
              Or read the full narrative now <v-icon x-small>mdi-arrow-right</v-icon>
            </router-link>
          </div>
        </article>
        <section v-if="primaryLocation" class="venue-map" :aria-label="`Map of ${eatery.name}`">
          <div class="venue-map-toolbar">
            <a :href="mapLink" target="_blank" rel="noopener noreferrer">Open full map <v-icon small>mdi-open-in-new</v-icon></a>
          </div>
          <iframe
            :title="`Map showing ${eatery.name}`"
            :src="mapEmbedUrl"
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
          ></iframe>
        </section>
      </v-container>
    </main>
    <main v-else class="missing-state">
      <v-container><h1>Experience not found</h1><router-link :to="{ name: 'reckon-way-eats' }">Return to Eats</router-link></v-container>
    </main>
    <AppFooter />
  </div>
</template>

<script>
import { mediaRef } from '@/services/mediaAssets';
import AppFooter from '@/components/Footer.vue';
import CultureNav from '@/components/reckon-way/culture/CultureNav.vue';
import { eatsBySlug } from '@/content/culture/eats';

const fallbackImage = mediaRef('adventures/reckon-way-featured-fallback.png');

export default {
  name: 'CultureEatDetailView',
  components: { AppFooter, CultureNav },
  data: () => ({ fallbackImage }),
  computed: {
    eatery() { return eatsBySlug[this.$route.params.eatSlug] || null; },
    primaryLocation() {
      return this.eatery?.locations?.[0] || this.eatery?.destinations?.[0] || '';
    },
    starLabel() {
      const count = this.eatery?.michelinStars || 0;
      return `${count} Michelin ${count === 1 ? 'Star' : 'Stars'}`;
    },
    mapQuery() { return [this.eatery?.name, this.eatery?.address, this.primaryLocation].filter(Boolean).join(', '); },
    mapEmbedUrl() { return `https://www.google.com/maps?q=${encodeURIComponent(this.mapQuery)}&output=embed`; },
    mapLink() {
      return this.eatery?.mapUrl
        || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.mapQuery)}`;
    },
  },
};
</script>

<style scoped>
.eat-detail-page,main{background:#050505}.eat-detail-page main{color:#fff;min-height:72vh;padding:44px 0 78px}.back-link{border-color:#444!important;color:#fff!important;margin-bottom:24px}.venue-card{background:linear-gradient(145deg,#17130e,#0b0b0b 70%);border:1px solid #403322;border-radius:14px;display:grid;grid-template-columns:minmax(0,1.05fr) minmax(0,.95fr);overflow:hidden}.experience-image{height:100%}.venue-content{padding:clamp(28px,5vw,58px)}.kicker{color:#e23232;font-family:"Oswald",sans-serif;font-size:.8rem;font-weight:900;letter-spacing:.09em;margin:0;text-transform:uppercase}h1,h2{font-family:"Oswald",sans-serif}h1{font-size:clamp(2.5rem,6vw,4.5rem);line-height:1;margin:12px 0 20px}.venue-summary{color:#d0d0d0;font-size:1.12rem;line-height:1.7}.venue-facts{border-top:1px solid #3a3023;display:grid;gap:16px;margin:26px 0 0;padding-top:22px}.venue-facts div{display:grid;gap:12px;grid-template-columns:105px 1fr}.venue-facts dt{color:#f0b13c;font-size:.7rem;font-weight:900;text-transform:uppercase}.venue-facts dd{color:#d4d4d4;margin:0}.venue-actions{display:flex;flex-wrap:wrap;gap:12px;margin-top:30px}.direct-story-link{align-items:center;color:#f0b13c;display:inline-flex;font-size:.82rem;font-weight:800;gap:6px;margin-top:20px;text-decoration:none}.direct-story-link .v-icon{color:inherit}.venue-map{background:#101010;border:1px solid #303030;border-radius:12px;margin-top:28px;overflow:hidden;padding:16px}.venue-map-toolbar{display:flex;justify-content:flex-end;padding:0 2px 12px}.venue-map a{color:#f0b13c;font-weight:800;text-decoration:none}.venue-map a .v-icon{color:inherit}.venue-map iframe{border:0;border-radius:8px;height:420px;width:100%}.missing-state{text-align:center}.missing-state a{color:#f0b13c}@media(max-width:820px){.venue-card{grid-template-columns:1fr}.experience-image{max-height:420px}.venue-facts div{grid-template-columns:90px 1fr}.venue-map iframe{height:300px}}
</style>
